import type { CreditBillingTimelineSummary } from '@nadoutong/contracts';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CUSTOMER_READ_ROLES, hasAnyOpsRole } from '../../auth/opsRoles.ts';
import { CreditContractDownload } from '../../components/CreditContractDownload.tsx';
import { CreditPaymentSummaryLine } from '../../components/credit/CreditBillingTimeline.tsx';
import { formatDisplayRateHint, formatLocalDate } from '../../components/formatters.ts';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';

/**
 * `credit_application` approval detail (#1957).
 *
 * The queue row summarizes the application as a number and a cycle, which is a
 * blind signature: the contract that justifies the limit lived only in Customer
 * 360, and the stage-2 signer had no way to reach it from here. This detail
 * carries the contract itself, read back through the same ops-api endpoint the
 * 360 page uses.
 *
 * The Customer 360 link is rendered ONLY for a session that may actually enter
 * that page, derived from `CUSTOMER_READ_ROLES` — the same constant `App.tsx`
 * guards the route with, so the two cannot drift apart.
 *
 * As of today no session can see this row and fail that check: a
 * `credit_application` is approved by sales-lead or finance-manager, and
 * sales-lead expands to customer-manager. The condition is therefore not
 * currently reachable in either direction, and it is kept as WIRING — the day
 * an approver role is added that cannot enter 360, this stays correct instead of
 * shipping a link that is guaranteed to 403. It is a presentation decision on
 * top of the server gate, never a replacement for it.
 */

interface CreditApplicationDetailProps {
  readonly orgId: string;
  readonly contractAttachmentKey: string;
  readonly appliedRate?: number | null;
  readonly reason?: string | null;
  readonly registeredAt?: string | null;
  readonly paymentSummary?: CreditBillingTimelineSummary | null;
}

export function CreditApplicationDetail({
  orgId,
  contractAttachmentKey,
  appliedRate = null,
  reason = null,
  registeredAt = null,
  paymentSummary = null,
}: CreditApplicationDetailProps): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const mayOpenCustomer360 = hasAnyOpsRole(user?.roles ?? [], CUSTOMER_READ_ROLES);

  return (
    <div className="flex flex-col gap-3" data-testid={`approval-credit-detail-${orgId}`}>
      <dl className="grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="text-label text-paper-low">{t('approvals.credit.registered_at')}</dt>
          <dd
            className="font-mono text-dense text-paper"
            data-testid="approval-credit-registered-at"
          >
            {registeredAt === null ? t('credit.overview.dash') : formatLocalDate(registeredAt)}
          </dd>
        </div>
        <div>
          <dt className="text-label text-paper-low">{t('approvals.credit.applied_rate')}</dt>
          <dd className="font-mono text-dense text-paper" data-testid="approval-credit-applied-rate">
            {formatDisplayRateHint(appliedRate)}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-label text-paper-low">{t('approvals.credit.reason')}</dt>
          <dd className="text-dense text-paper" data-testid="approval-credit-reason">
            {reason === null || reason.length === 0
              ? t('approvals.credit.reason_unset')
              : reason}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-label text-paper-low">{t('approvals.credit.payment_summary')}</dt>
          <dd data-testid="approval-credit-payment-summary">
            {paymentSummary === null ? (
              t('credit.overview.dash')
            ) : (
              <CreditPaymentSummaryLine summary={paymentSummary} />
            )}
          </dd>
        </div>
      </dl>
      <CreditContractDownload
        orgId={orgId}
        contractAttachmentKey={contractAttachmentKey}
        testId="approval-credit-contract"
      />
      {mayOpenCustomer360 ? (
        <Link
          to={`/crm/customers/${encodeURIComponent(orgId)}`}
          className="inline-flex w-fit items-center gap-1.5 text-dense text-signal hover:underline"
          data-testid="approval-credit-customer-360-link"
        >
          <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
          {t('approvals.credit.open_customer_360')}
        </Link>
      ) : null}
    </div>
  );
}
